import type { LearnTopic } from "../topic-types";
import { IFS_INTRO, IFS_SELF_QUALITIES, IFS_NO_BAD_PARTS, IFS_SAFETY } from "../ifs";

/**
 * Internal Family Systems (IFS).
 *
 * Add new guides, exercises, prompts, worksheets or audio by appending to
 * the `resources` array of a section — or by adding a whole new section.
 * Nothing else needs to change.
 */
export const IFS_TOPIC: LearnTopic = {
  slug: "ifs",
  emoji: "🌿",
  title: "Internal Family Systems (IFS)",
  tagline: "Understand your inner system",
  theme: "lavender",
  category: "ifs",
  cardBlurb:
    "Parts, protectors, exiles and Self — a gentle learning pathway through parts work.",
  continueTo: [
    { label: "Inner Child Work", to: "/learn/inner-child" },
    { label: "Working With Your Inner Critic", to: "/learn/inner-critic" },
    { label: "Shame-Based Trauma", to: "/learn/shame-based-trauma" },
    { label: "Open my journal", to: "/journal" },
  ],
  seoDescription:
    "Learn about the different parts of your internal system and how protective patterns can develop in response to difficult experiences. Trauma-informed IFS guides, exercises and worksheets from Recovery Trauma™.",
  intro: [
    "Learn about the different parts of your internal system and how protective patterns can develop in response to difficult experiences.",
    "Explore IFS through easy-to-understand educational guides, reflective exercises and practical worksheets.",
    "You may recognise some of what follows, and some of it may not fit you at all. There is nothing here to get right — this is an invitation to be curious, at whatever pace feels manageable.",
  ],
  closing:
    "IFS is an educational framework, not a diagnosis or a replacement for professional mental health treatment. If parts work stirs up more than feels manageable, it can help to explore it alongside a trauma-informed therapist.",
  sections: [
    {
      slug: "understanding-ifs",
      emoji: "📚",
      title: "Understanding IFS",
      blurb: "What parts are, why they develop, and the Self underneath them all.",
      intro:
        "Plain-English education first. Nothing here asks you to look inward yet — you can simply read.",
      resources: [
        {
          slug: "what-is-ifs",
          kind: "learn",
          title: "What is Internal Family Systems?",
          summary: "The core idea: you are a system of parts, not one single mind.",
          minutes: 4,
          blocks: [
            { type: "prose", text: IFS_INTRO },
            { type: "visual", name: "ifs-parts" },
            {
              type: "list",
              title: "Some ideas people find helpful",
              items: [
                "Every part has a job, and that job began as protection",
                "Parts are not symptoms to remove — they are responses that made sense",
                "Self is not a part; it is what remains when parts step back",
                "Nothing inside you is an enemy, even the patterns you dislike most",
              ],
            },
            {
              type: "note",
              text: "IFS is one way of understanding an inner world. If a different language fits you better, that is completely fine.",
            },
          ],
        },
        {
          slug: "understanding-parts",
          kind: "learn",
          title: "Understanding “parts”",
          summary: "What people mean by a part, and how parts show up day to day.",
          minutes: 4,
          blocks: [
            {
              type: "prose",
              text: "A part is an inner voice, feeling, urge or role that behaves as though it has its own point of view. Most people already speak this way: “part of me wants to go, part of me wants to stay.” In IFS that phrase is taken seriously rather than as a figure of speech.",
            },
            {
              type: "list",
              title: "Parts can show up as",
              items: [
                "A familiar inner commentary that arrives at the same moments",
                "A body sensation that appears before you have words for it",
                "A sudden urge that feels older or bigger than the situation",
                "A mood that seems to switch faster than circumstances did",
                "A pattern you have promised yourself you would stop repeating",
              ],
            },
            {
              type: "prose",
              text: "Noticing a part is not the same as being taken over by it. Some people describe the shift as moving from “I am furious” to “something in me is furious” — a small amount of space that makes curiosity possible.",
            },
          ],
        },
        {
          slug: "the-self",
          kind: "learn",
          title: "The Self in IFS",
          summary: "The calm, curious presence that parts can relax around.",
          minutes: 3,
          blocks: [
            {
              type: "prose",
              text: "IFS describes Self as the part of you that was never damaged — steady, curious and compassionate. It can be crowded out by protection, but it is not created, earned or repaired. Most people meet it in glimpses first.",
            },
            { type: "list", title: "Qualities often associated with Self", items: [...IFS_SELF_QUALITIES] },
            { type: "quote", text: "Self can't be broken — only crowded out." },
          ],
        },
        {
          slug: "protectors",
          kind: "learn",
          title: "Protectors",
          summary: "Why some parts spend their whole existence keeping pain away.",
          minutes: 3,
          blocks: [
            {
              type: "prose",
              text: "Protectors are the parts whose role is to prevent overwhelming feeling from reaching you. IFS describes two broad styles: managers, who work ahead of time, and firefighters, who react once pain breaks through.",
            },
            {
              type: "list",
              title: "What protectors have in common",
              items: [
                "They formed at a moment when something felt unbearable",
                "They are usually much older than the situation you are in now",
                "They rarely respond to being argued with or overridden",
                "They tend to soften when they feel understood rather than removed",
              ],
            },
            { type: "prose", text: IFS_NO_BAD_PARTS },
          ],
        },
        {
          slug: "managers",
          kind: "learn",
          title: "Managers",
          summary: "Proactive protection: planning, perfecting, pleasing, controlling.",
          minutes: 3,
          blocks: [
            {
              type: "prose",
              text: "Managers work tirelessly so that pain never arrives in the first place. They organise, anticipate, perfect and please, keeping life predictable enough that nothing overwhelming gets through.",
            },
            {
              type: "list",
              title: "Some people recognise",
              items: [
                "The perfectionist for whom nothing is ever quite finished",
                "The people-pleaser who reads the room before speaking",
                "The inner critic who gets there before anyone else can",
                "The planner who cannot rest until everything is handled",
                "The one who keeps you busy, small, or endlessly useful",
              ],
            },
            { type: "quote", text: "“If I stay ahead of it, we'll be safe.”" },
          ],
        },
        {
          slug: "firefighters",
          kind: "learn",
          title: "Firefighters",
          summary: "Reactive protection: whatever stops the feeling, fast.",
          minutes: 3,
          blocks: [
            {
              type: "prose",
              text: "When pain breaks through anyway, firefighters act to put the feeling out immediately. Their methods can look destructive from the outside, but the intention underneath is always to stop something that feels unbearable.",
            },
            {
              type: "list",
              title: "This can look like",
              items: [
                "Numbing: scrolling, drinking, bingeing, shopping, overworking",
                "Sudden anger, or the urge to leave and not come back",
                "Going blank, floating away, losing time",
                "Risk-taking or compulsive behaviour",
                "Shutting people out the moment they get close",
              ],
            },
            {
              type: "note",
              text: "If a firefighter's methods put you at risk, that part still deserves understanding — and you deserve support alongside it. Our Self-Harm Support and Crisis Resources pages are always open.",
            },
          ],
        },
        {
          slug: "exiles",
          kind: "learn",
          title: "Exiles",
          summary: "The younger parts carrying what was too much at the time.",
          minutes: 3,
          blocks: [
            {
              type: "prose",
              text: "Exiles hold the fear, shame, grief and loneliness that were too much to feel when they happened, so they were kept out of everyday awareness. Protectors exist largely to keep those feelings from flooding you, which is why approaching an exile without a protector's agreement often backfires.",
            },
            {
              type: "list",
              title: "You might notice",
              items: [
                "A wave of hurt that feels far bigger than the moment",
                "Shame that says something is wrong with you rather than with what happened",
                "Fear of being abandoned, or of being truly seen",
                "Grief arriving without warning",
                "Feeling very small inside an adult life",
              ],
            },
            { type: "prose", text: IFS_SAFETY },
          ],
        },
        {
          slug: "why-parts-develop",
          kind: "learn",
          title: "Why parts develop",
          summary: "Protection as an intelligent response to real circumstances.",
          minutes: 3,
          blocks: [
            {
              type: "prose",
              text: "Parts tend to take on their roles at moments when something was needed and unavailable — safety, comfort, predictability, permission to feel. The role that formed then often keeps running long after the circumstances change.",
            },
            {
              type: "steps",
              title: "A common sequence",
              items: [
                { label: "Something overwhelming happens", note: "Often repeatedly, often young." },
                { label: "A feeling has nowhere safe to go", note: "So it is set aside." },
                { label: "A part takes on a job", note: "Watch, perfect, please, numb, disappear." },
                { label: "The job works", note: "Which is exactly why it stays." },
                { label: "Life changes; the job does not", note: "The pattern arrives in situations that no longer need it." },
              ],
            },
          ],
        },
        {
          slug: "parts-and-trauma-responses",
          kind: "learn",
          title: "Parts and trauma responses",
          summary: "How fight, flight, freeze and fawn map onto protective parts.",
          minutes: 4,
          blocks: [
            {
              type: "prose",
              text: "Survival responses and protective parts describe overlapping ground from different angles. One is about what the nervous system does; the other is about the inner role that grew around it.",
            },
            {
              type: "list",
              title: "Loose overlaps some people notice",
              items: [
                "Fight — protectors who confront, criticise or push back first",
                "Flight — protectors who keep busy, plan, achieve, or leave early",
                "Freeze — protectors who go still, blank or quiet until it passes",
                "Fawn — protectors who accommodate, soothe and keep others comfortable",
              ],
            },
            {
              type: "note",
              text: "These are patterns to explore, not categories to be placed in. Most people recognise several, in different relationships and at different times.",
            },
          ],
        },
      ],
    },
    {
      slug: "meet-your-parts",
      emoji: "🔎",
      title: "Getting to know your parts",
      blurb: "Gentle noticing: what a part fears, protects, and needs.",
      intro:
        "This section moves from reading to noticing. Go slowly, and stop whenever you want to — stopping is a valid outcome, not a failure.",
      resources: [
        {
          slug: "identifying-your-parts",
          kind: "reflect",
          title: "Identifying your parts",
          summary: "Beginning to notice the voices and patterns already present.",
          minutes: 6,
          blocks: [
            {
              type: "prose",
              text: "You do not have to find every part, or name them correctly. Start with the one that showed up most recently.",
            },
            {
              type: "prompts",
              title: "Reflection prompts",
              items: [
                "Which inner voice have I heard most often this week?",
                "If that voice had an age, how old might it feel?",
                "Where do I notice it in my body?",
                "When does it get louder, and when does it rest?",
                "What tone does it use with me — and does that tone remind me of anyone?",
              ],
            },
          ],
        },
        {
          slug: "what-triggers-this-part",
          kind: "reflect",
          title: "What triggers this part?",
          summary: "Tracing the situations that call a protector forward.",
          minutes: 5,
          blocks: [
            {
              type: "prompts",
              items: [
                "What was happening in the moments just before this part appeared?",
                "Was there a look, a tone of voice, a silence, or a delay?",
                "Does this trigger repeat in a particular kind of relationship?",
                "How quickly did it arrive — instantly, or building over time?",
                "What does the timing suggest this part is watching for?",
              ],
            },
          ],
        },
        {
          slug: "what-is-this-part-protecting",
          kind: "reflect",
          title: "What is this part trying to protect?",
          summary: "Looking underneath the behaviour to the intention.",
          minutes: 6,
          blocks: [
            {
              type: "prose",
              text: "Protective behaviour usually makes more sense once its purpose is visible. You might ask the part directly, or simply wonder about it.",
            },
            {
              type: "prompts",
              items: [
                "If this part stopped doing its job tomorrow, what does it think would happen?",
                "What feeling might it be standing in front of?",
                "How long has it been doing this?",
                "How old was I when it started?",
                "What might it need to hear to feel less alone in the job?",
              ],
            },
          ],
        },
        {
          slug: "what-does-this-part-fear-and-need",
          kind: "reflect",
          title: "What does this part fear — and need?",
          summary: "Two questions that often soften a protector.",
          minutes: 5,
          blocks: [
            {
              type: "prompts",
              title: "Fear",
              items: [
                "What is this part most afraid of happening?",
                "Who or what would it be afraid of losing?",
                "What is it afraid I would find out?",
              ],
            },
            {
              type: "prompts",
              title: "Need",
              items: [
                "What would help this part feel less on duty?",
                "Does it need acknowledgement, rest, information, or company?",
                "What is one small thing I could offer it today?",
              ],
            },
          ],
        },
        {
          slug: "conflicting-parts",
          kind: "learn",
          title: "Recognising conflicting parts",
          summary: "When two parts want opposite things at the same time.",
          minutes: 3,
          blocks: [
            {
              type: "prose",
              text: "Inner conflict is often described as indecision or self-sabotage. In IFS it usually means two protectors with different strategies are both trying to keep you safe.",
            },
            {
              type: "list",
              title: "Common pairings people notice",
              items: [
                "A part that wants closeness and a part that wants distance",
                "A part that pushes hard and a part that shuts everything down",
                "A part that wants to speak and a part that keeps the peace",
                "A part that longs to rest and a part that says rest is unsafe",
              ],
            },
            {
              type: "prose",
              text: "Rather than choosing a side, it can help to acknowledge both jobs. Conflict tends to ease when neither part is being overruled.",
            },
          ],
        },
        {
          slug: "understanding-protective-behaviours",
          kind: "learn",
          title: "Understanding protective behaviours",
          summary: "Separating a behaviour from the intention underneath it.",
          minutes: 3,
          blocks: [
            {
              type: "steps",
              title: "One way to look at it",
              items: [
                { label: "The behaviour", note: "What actually happens, described without judgement." },
                { label: "The moment before", note: "What was felt or sensed just beforehand." },
                { label: "The job", note: "What the behaviour prevents, delays or hides." },
                { label: "The cost", note: "What it takes from you now, honestly and gently." },
                { label: "The alternative", note: "What might meet the same need with less cost." },
              ],
            },
            {
              type: "note",
              text: "Changing a protective behaviour rarely works by force. Understanding it first is not a delay — for most people it is the route.",
            },
          ],
        },
      ],
    },
    {
      slug: "exercises",
      emoji: "🌿",
      title: "IFS exercises",
      blurb: "Guided, step-by-step practices you can try at your own pace.",
      intro:
        "Each exercise can be paused at any point. Settling the body first — breathing or grounding — often makes parts easier to notice.",
      resources: [
        {
          slug: "meet-a-part",
          kind: "practice",
          title: "Meet a part",
          summary: "A gentle step-by-step reflection for noticing a single part.",
          minutes: 10,
          blocks: [
            {
              type: "prose",
              text: "Choose a part that feels manageable today — not the most painful one. You can stop after any step.",
            },
            {
              type: "steps",
              items: [
                { label: "Find it", note: "Notice where in your body, or in your thinking, this part shows up." },
                { label: "Focus", note: "Let your attention rest there without trying to change anything." },
                { label: "Flesh it out", note: "Does it have an age, a tone, an image, a temperature?" },
                { label: "Feel toward it", note: "Notice how you feel toward this part. If it is irritation, that is another part — you can ask it for a little space." },
                { label: "Befriend it", note: "Ask what its job is, and how long it has been doing it." },
                { label: "Fears", note: "Ask what it is afraid would happen if it stopped." },
                { label: "Close gently", note: "Thank it. Let it know you are not asking it to leave." },
              ],
            },
            {
              type: "note",
              text: "If at any point the feeling becomes too big, this is a good moment to stop, breathe, and come back another day.",
            },
          ],
        },
        {
          slug: "parts-mapping",
          kind: "practice",
          title: "Parts mapping",
          summary: "Map a part from name through to what it needs.",
          minutes: 12,
          blocks: [
            {
              type: "prose",
              text: "Mapping puts a part on paper so it is not being held entirely inside. Work through the chain for one part at a time.",
            },
            {
              type: "flow",
              items: [
                "Part name",
                "Emotion",
                "Trigger",
                "Protective behaviour",
                "What it fears",
                "What it needs",
              ],
            },
            {
              type: "prose",
              text: "Some people map two or three parts and notice they are in conversation with each other. The printable My Parts Map worksheet gives you space for this.",
            },
          ],
        },
        {
          slug: "conversation-with-a-part",
          kind: "reflect",
          title: "A conversation with a part",
          summary: "Journal-style prompts for exploring a part with curiosity.",
          minutes: 15,
          blocks: [
            {
              type: "prose",
              text: "Write both sides if it helps — your question, then whatever answer arrives, without editing it.",
            },
            {
              type: "prompts",
              items: [
                "How long have you been doing this job?",
                "What would you like me to understand about it?",
                "What do you wish I would stop doing?",
                "What are you afraid would happen if you rested?",
                "What would you need from me to feel less alone in this?",
                "Is there anything you have been waiting a long time to say?",
              ],
            },
          ],
        },
        {
          slug: "guided-parts-practice",
          kind: "audio",
          title: "Guided parts practice",
          summary: "An audio version of Meet a Part, coming to this section.",
          status: "soon",
        },
      ],
    },
    {
      slug: "worksheets",
      emoji: "📄",
      title: "IFS worksheets",
      blurb: "Printable pages to work through on paper, at your own pace.",
      intro:
        "Every worksheet can be printed from your browser, or copied into your journal. Nothing you write is stored on this page.",
      resources: [
        {
          slug: "my-parts-map",
          kind: "worksheet",
          title: "My Parts Map",
          summary: "One page per part: name, emotion, trigger, behaviour, fear, need.",
          worksheetFields: [
            { label: "Part name", hint: "Whatever you would call it — no need for it to be clever." },
            { label: "Emotion it carries", lines: 2 },
            { label: "What triggers it", lines: 2 },
            { label: "Protective behaviour", lines: 2 },
            { label: "What it fears", lines: 2 },
            { label: "What it needs", lines: 2 },
          ],
        },
        {
          slug: "understanding-my-protectors",
          kind: "worksheet",
          title: "Understanding My Protectors",
          summary: "A wider view of the protectors you notice most often.",
          worksheetFields: [
            { label: "Protector I notice most", lines: 2 },
            { label: "When it steps forward", lines: 3 },
            { label: "What it does well", lines: 2 },
            { label: "What it costs me", lines: 2 },
            { label: "What I would like it to know", lines: 3 },
          ],
        },
        {
          slug: "manager-parts",
          kind: "worksheet",
          title: "Manager Parts",
          summary: "For the planning, perfecting and pleasing protectors.",
          worksheetFields: [
            { label: "What this manager organises or prevents", lines: 3 },
            { label: "The rules it lives by", lines: 3 },
            { label: "What happens when the rules are broken", lines: 2 },
            { label: "One rule that could soften slightly", lines: 2 },
          ],
        },
        {
          slug: "firefighter-parts",
          kind: "worksheet",
          title: "Firefighter Parts",
          summary: "For the parts that act fast when pain breaks through.",
          worksheetFields: [
            { label: "What this part does when the feeling arrives", lines: 3 },
            { label: "What it is putting out", lines: 2 },
            { label: "How I feel toward it afterwards", lines: 2 },
            { label: "What might help before it reaches that point", lines: 3 },
            { label: "Support I can reach for", hint: "People, pages, phone numbers.", lines: 2 },
          ],
        },
        {
          slug: "exploring-exiles",
          kind: "worksheet",
          title: "Exploring Exiles",
          summary: "A slow, optional page. Only with a protector's agreement.",
          worksheetFields: [
            { label: "Have my protectors agreed to this?", hint: "If not, it is fine to stop here." },
            { label: "What this younger part carries", lines: 3 },
            { label: "How old it feels", lines: 1 },
            { label: "What it never got to hear", lines: 3 },
            { label: "How I will look after myself afterwards", lines: 2 },
          ],
        },
        {
          slug: "trigger-part-response",
          kind: "worksheet",
          title: "Trigger → Part → Response",
          summary: "Track one episode from trigger to aftermath.",
          worksheetFields: [
            { label: "Situation", lines: 2 },
            { label: "Trigger", lines: 2 },
            { label: "Part that responded", lines: 1 },
            { label: "What it did", lines: 2 },
            { label: "What I needed in that moment", lines: 2 },
          ],
        },
        {
          slug: "what-is-this-part-protecting-worksheet",
          kind: "worksheet",
          title: "What Is This Part Protecting?",
          summary: "One question, given a whole page.",
          worksheetFields: [
            { label: "The part", lines: 1 },
            { label: "What it stands in front of", lines: 4 },
            { label: "What it believes would happen without it", lines: 3 },
            { label: "What I would like to say to it", lines: 3 },
          ],
        },
        {
          slug: "getting-to-know-a-part",
          kind: "worksheet",
          title: "Getting to Know a Part",
          summary: "A printable version of the Meet a Part exercise.",
          worksheetFields: [
            { label: "Where I notice it", lines: 2 },
            { label: "Age, tone or image", lines: 2 },
            { label: "How I feel toward it", lines: 2 },
            { label: "Its job", lines: 2 },
            { label: "Its fear", lines: 2 },
            { label: "What I said to it", lines: 3 },
          ],
        },
        {
          slug: "self-vs-part-reflection",
          kind: "worksheet",
          title: "Self vs Part Reflection",
          summary: "Noticing the difference between speaking from Self and from a part.",
          worksheetFields: [
            { label: "What the part is saying", lines: 3 },
            { label: "What I notice when I have a little more space", lines: 3 },
            { label: "Which qualities of Self are present right now", hint: "Calm, curiosity, compassion, clarity, courage, confidence, creativity, connectedness.", lines: 2 },
            { label: "What changes when Self is present", lines: 3 },
          ],
        },
      ],
    },
  ],
};
